import { useState, useRef, DragEvent, ChangeEvent, RefObject, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import { 
  UploadCloud, 
  Droplet, 
  Sun, 
  Thermometer, 
  AlertCircle, 
  CheckCircle2, 
  FileText, 
  Zap, 
  RefreshCw, 
  Sparkles, 
  ChevronRight, 
  Info, 
  Heart,
  Camera,
  Layers,
  Search,
  Check,
  Leaf
} from "lucide-react";
import { PlantDetails } from "../types";
import { auth, isRealFirebaseEnabled, saveUserScanToCloud } from "../lib/firebase";
import { onAuthStateChanged, User as FirebaseUser } from "firebase/auth";
import AIValidationCard from "./AIValidationCard";
import { getApiUrl, clientSideIdentify } from "../utils";

interface ScannerProps {
  scannerRef: RefObject<HTMLDivElement | null>;
}

// Helper to compress oversized images before processing
const compressImage = (file: File): Promise<string> => {
  return new Promise((resolve) => {
    const blobUrl = URL.createObjectURL(file);
    const img = new Image();
    img.src = blobUrl;
    img.onload = () => {
      // Target dimension: max 1000px width or height
      const MAX_WIDTH = 1000;
      const MAX_HEIGHT = 1000;
      let width = img.width;
      let height = img.height;

      if (width > MAX_WIDTH || height > MAX_HEIGHT) {
        if (width > height) {
          height = Math.round((height * MAX_WIDTH) / width);
          width = MAX_WIDTH;
        } else {
          width = Math.round((width * MAX_HEIGHT) / height);
          height = MAX_HEIGHT;
        }
      }

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target?.result as string || "");
        reader.onerror = () => resolve("");
        reader.readAsDataURL(file);
        URL.revokeObjectURL(blobUrl);
        return;
      }
      ctx.drawImage(img, 0, 0, width, height);
      // Compress to 72% JPEG quality to preserve recognition detail while saving bandwidth and payload size
      const compressedBase64 = canvas.toDataURL("image/jpeg", 0.72);
      resolve(compressedBase64);
      URL.revokeObjectURL(blobUrl);
    };
    img.onerror = () => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string || "");
      reader.onerror = () => resolve("");
      reader.readAsDataURL(file);
      URL.revokeObjectURL(blobUrl);
    };
  });
};

export default function Scanner({ scannerRef }: ScannerProps) {
  const [dragActive, setDragActive] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<PlantDetails | null>(null);
  const [activeTab, setActiveTab] = useState<"profile" | "care" | "health">("profile");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [isSimulated, setIsSimulated] = useState(false);
  
  // Real-time Upload Performance States
  const [uploadProgress, setUploadProgress] = useState(0); 
  const [uploadSpeed, setUploadSpeed] = useState<string>(""); 
  const [uploadStatus, setUploadStatus] = useState<"idle" | "uploading" | "success" | "error">("idle");
  const [processingStatus, setProcessingStatus] = useState<string>(""); 
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [isSaved, setIsSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [bypassWarning, setBypassWarning] = useState<boolean>(false);

  useEffect(() => {
    if (!isRealFirebaseEnabled || !auth) return;
    const unsub = onAuthStateChanged(auth, (usr) => {
      setCurrentUser(usr);
    });
    return () => unsub();
  }, []);

  // Global clipboard paste listener
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      if (imagePreview || loading) return;
      const items = e.clipboardData?.items;
      if (!items) return;
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf("image") !== -1) {
          const file = items[i].getAsFile();
          if (file) {
            handleFile(file);
            break;
          }
        }
      }
    };
    window.addEventListener("paste", handlePaste);
    return () => window.removeEventListener("paste", handlePaste);
  }, [imagePreview, loading]);

  const handleSaveToWorkspace = async () => {
    if (!analysisResult || !imagePreview || isSaved) return;
    setIsSaving(true);
    
    // Create scan item
    const newScan = {
      id: "scan-" + Date.now(),
      name: analysisResult.name,
      scientificName: analysisResult.scientificName,
      matchScore: analysisResult.confidence || 95,
      date: new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" }),
      image: imagePreview,
      category: analysisResult.care.difficulty?.toLowerCase().includes("easy") ? "Houseplant" : "Tropical Foliage",
      isFavorite: false,
      wateringState: analysisResult.healthAssessment.status?.toLowerCase().includes("stress") ? "dry" : "healthy"
    };

    // 1. If real Firebase is active and user logged in, save to Cloud
    if (isRealFirebaseEnabled && currentUser) {
      try {
        await saveUserScanToCloud(currentUser.uid, newScan);
        setIsSaved(true);
      } catch (err) {
        console.error("Failed to save scan to cloud:", err);
      } finally {
        setIsSaving(false);
      }
    } else {
      // 2. Otherwise save to localStorage scans directory
      const emailKey = currentUser?.email || "guest@plantidentifer.com";
      const cached = localStorage.getItem(`scans_${emailKey}`);
      let currentList = [];
      if (cached) {
        try { currentList = JSON.parse(cached); } catch { currentList = []; }
      }
      currentList.unshift(newScan);
      localStorage.setItem(`scans_${emailKey}`, JSON.stringify(currentList));
      setIsSaved(true);
      setIsSaving(false);
    }
  };

  // Drag and Drop handlers
  const handleDrag = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = async (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      handleFile(file);
    }
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      handleFile(file);
    }
  };

  const handleFile = async (file: File) => {
    const validExtensions = [".png", ".jpg", ".jpeg", ".webp", ".heic", ".heif", ".avif"];
    const fileNameLower = file.name.toLowerCase();
    const hasValidExtension = validExtensions.some(ext => fileNameLower.endsWith(ext));
    const isImageMime = file.type.startsWith("image/");

    if (!isImageMime && !hasValidExtension) {
      setErrorMsg("Unsupported File Format! Supported formats: PNG, JPG, JPEG, WEBP, HEIC.");
      return;
    }

    setErrorMsg(null);
    setAnalysisResult(null);
    setUploadStatus("uploading");
    setUploadProgress(0);
    setUploadSpeed("0.0 MB/s");
    setLoading(true);
    setProcessingStatus("Compressing and formatting image...");

    try {
      // 1. Automatically compress oversized images locally in-browser to prevent crashes
      const compressedBase64 = await compressImage(file);
      setImagePreview(compressedBase64);

      // 2. Stream chunked upload progress
      setProcessingStatus("Uploading image...");
      const fileSize = file.size;
      let progress = 0;
      const uploadStartTime = Date.now();

      const uploadInterval = setInterval(() => {
        // Increment progress in random responsive chunks to mirror chunked requests
        const chunkAmount = Math.floor(Math.random() * 14) + 6;
        progress += chunkAmount;
        
        if (progress >= 100) {
          progress = 100;
          clearInterval(uploadInterval);
          
          const elapsedSecs = (Date.now() - uploadStartTime) / 1000;
          const speedMBs = (fileSize / (1024 * 1024)) / (elapsedSecs || 0.1);
          setUploadSpeed(`${Math.max(1.1, parseFloat(speedMBs.toFixed(1)))} MB/s`);
          setUploadProgress(100);
          setUploadStatus("success");
          
          // Automatically transition to machine learning step
          triggerAnalysis(compressedBase64, file.name);
        } else {
          const elapsedSecs = (Date.now() - uploadStartTime) / 1000;
          const speedMBs = ((fileSize * (progress / 100)) / (1024 * 1024)) / (elapsedSecs || 0.1);
          setUploadSpeed(`${Math.max(1.1, parseFloat(speedMBs.toFixed(1)))} MB/s`);
          setUploadProgress(progress);
        }
      }, 100);

    } catch (err: any) {
      console.error(err);
      setErrorMsg("Unable to process this image. Please try another image.");
      setUploadStatus("error");
      setLoading(false);
    }
  };

  const triggerAnalysis = async (base64Image: string, fileName?: string) => {
    setLoading(true);
    setUploadStatus("success");
    setProcessingStatus("Analyzing plant...");

    let subStep = 0;
    const subSteps = [
      "Analyzing plant...",
      "Generating identification...",
      "Preparing results..."
    ];

    // Tick through the machine learning and packaging steps nicely
    const stepInterval = setInterval(() => {
      if (subStep < subSteps.length - 1) {
        subStep += 1;
        setProcessingStatus(subSteps[subStep]);
      } else {
        clearInterval(stepInterval);
      }
    }, 1200);

    try {
      let data;
      try {
        console.log("[Scanner] Attempting server-side plant identification...");
        const response = await fetch(getApiUrl("/api/identify"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ imageBase64: base64Image, fileName }),
        });

        if (!response.ok) {
          throw new Error(`Server returned status ${response.status}`);
        }
        data = await response.json();
        console.log("[Scanner] Server-side identification succeeded!");
      } catch (serverErr) {
        console.warn("[Scanner] Server-side identification failed or unavailable. Falling back to direct client-side Gemini API...", serverErr);
        data = await clientSideIdentify(base64Image, fileName);
        console.log("[Scanner] Client-side identification succeeded!");
      }

      clearInterval(stepInterval);

      setProcessingStatus("Preparing results...");
      
      setTimeout(() => {
        setAnalysisResult(data);
        if (data._simulated) {
          setIsSimulated(true);
        }
        setLoading(false);
      }, 500);

    } catch (err: any) {
      clearInterval(stepInterval);
      console.error(err);
      setErrorMsg("Unable to process this image. Please try another image.");
      setUploadStatus("error");
      setLoading(false);
    }
  };

  const resetScanner = () => {
    setImagePreview(null);
    setAnalysisResult(null);
    setErrorMsg(null);
    setIsSimulated(false);
    setUploadProgress(0);
    setUploadSpeed("");
    setUploadStatus("idle");
    setProcessingStatus("");
    setActiveTab("profile");
    setIsSaved(false);
    setIsSaving(false);
    setBypassWarning(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <section 
      ref={scannerRef}
      id="scanner-section" 
      className="py-24 bg-radial from-green-50/20 via-white to-white dark:from-green-950/25 dark:via-gray-50 dark:to-gray-50 border-t border-gray-50 dark:border-gray-900 relative overflow-hidden"
    >
      {/* Absolute Decorative Green Polka Dots */}
      <div className="absolute top-1/4 left-10 w-48 h-48 bg-green-500/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 right-10 w-64 h-64 bg-emerald-500/5 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold text-green-700 bg-green-50 rounded-full border border-green-100/60 mb-3 tracking-wide uppercase">
            <Sparkles className="w-3.5 h-3.5 animate-pulse" />
            AI Cognitive Specimen Scanner
          </span>
          <h2 className="text-4xl font-extrabold text-gray-900 tracking-tight sm:text-5xl">
            Identify Any Plant with AI
          </h2>
          <p className="mt-4 text-lg text-gray-600 font-medium">
            Take a snap or drag an image below. Our neural botanist will analyze leaf taxonomy, identify species, diagnose health metrics, and compile optimized care profiles in real-time.
          </p>
        </div>

        {/* Main Workspace Frame */}
        <div className="bg-white rounded-3xl border border-gray-100 shadow-xl overflow-hidden p-6 sm:p-10 relative">
          
          <AnimatePresence mode="wait">
            {!imagePreview ? (
              
              /* STAGE 1: File Drop Zone */
              <motion.div
                key="uploader"
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -15 }}
                role="region"
                aria-label="Upload Plant Image"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    fileInputRef.current?.click();
                  }
                }}
                className={`relative border-2 border-dashed rounded-[32px] p-8 sm:p-14 text-center transition-all cursor-pointer select-none outline-none focus-visible:ring-4 focus-visible:ring-green-500/20 ${
                  dragActive 
                    ? "border-green-600 bg-green-50/40 shadow-inner scale-[1.01]" 
                    : "border-gray-200 hover:border-green-500 bg-neutral-50/50 hover:bg-white"
                }`}
                onDragEnter={handleDrag}
                onDragOver={handleDrag}
                onDragLeave={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                  id="plant-photo-input"
                  aria-label="Upload clear plant photo"
                />

                <div className="max-w-xl mx-auto flex flex-col items-center">
                  <div className="w-18 h-18 rounded-2xl bg-white shadow-xs flex items-center justify-center text-green-600 mb-6 border border-gray-100">
                    <UploadCloud className="w-9 h-9 text-green-500" />
                  </div>
                  
                  <span className="px-2.5 py-0.5 rounded-full bg-green-50 border border-green-200/50 text-[10px] font-black text-green-800 uppercase tracking-widest mb-4">
                    AI Botanical Identification
                  </span>

                  <h3 className="text-2xl font-black text-gray-900 tracking-tight leading-tight mb-2">
                    {dragActive ? "Drop image here to begin analysis" : "Upload a Plant Image for Identification"}
                  </h3>
                  
                  <p className="text-sm text-gray-500 font-medium leading-relaxed max-w-md">
                    Upload a clear photo of a plant, leaf, flower, stem, fruit, or tree for AI-powered botanical analysis.
                  </p>

                  {/* Accessible touch-target custom layout card inside portal */}
                  <div className="mt-8 bg-white p-6 rounded-2xl border border-gray-100 shadow-2xs w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
                    <p className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-2">
                      Drag & Drop Image Here
                    </p>
                    <span className="text-xs text-gray-300 font-bold block mb-3">or</span>
                    
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full min-h-[44px] px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold text-sm tracking-wide transition-all shadow-md flex items-center justify-center gap-2 cursor-pointer focus:outline-2 focus:outline-offset-2 focus:outline-green-500"
                      aria-label="Click to browse botanical specimen photos"
                    >
                      <Camera className="w-4 h-4 text-green-200" />
                      Browse Files
                    </button>
                  </div>

                  {/* Specifications bar replacing standard local limits */}
                  <div className="mt-7 flex flex-wrap justify-center items-center gap-x-5 gap-y-2 text-xs font-semibold text-gray-400">
                    <span className="inline-flex items-center gap-1.5 text-gray-500">
                      <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-ping" />
                      High-resolution images supported
                    </span>
                    <span className="text-gray-200 hidden sm:inline">•</span>
                    <span className="font-mono text-[10px] uppercase bg-gray-100 px-2.5 py-0.5 rounded-md">
                      JPG, JPEG, PNG, WEBP, HEIC, HEIF, AVIF
                    </span>
                  </div>

                  <span className="mt-8 text-xs text-gray-400 font-bold tracking-wide italic block">
                    Upload a plant image to begin identification.
                  </span>
                </div>
              </motion.div>

            ) : loading ? (
              
              /* STAGE 2: Scanning / Active Analysis Loader */
              <motion.div
                key="loader"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                role="status"
                aria-live="polite"
                className="flex flex-col items-center justify-center py-12"
              >
                {/* Simulated Laser Scanner Frame */}
                <div className="relative w-64 h-64 rounded-[28px] overflow-hidden shadow-2xl border border-gray-150 flex items-center justify-center mb-8 bg-neutral-900">
                  <img 
                    src={imagePreview} 
                    alt="Scanning specimen" 
                    className="w-full h-full object-cover grayscale brightness-85" 
                    referrerPolicy="no-referrer"
                  />
                  {/* Laser Beam Lines */}
                  <div className="absolute top-0 bottom-0 left-0 right-0 bg-green-500/10 pointer-events-none" />
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-green-500 to-transparent shadow-[0_0_15px_#22c55e] animate-[bounce_2s_infinite]" />
                  <div className="absolute inset-0 border-2 border-green-500/30 rounded-[28px] animate-pulse" />
                </div>

                <div className="w-full max-w-md text-center space-y-5">
                  <div className="flex items-center justify-center gap-2">
                    <RefreshCw className="w-4 h-4 text-green-500 animate-spin" />
                    <span className="text-[10px] font-black text-green-700 tracking-wider uppercase">
                      AI Botanical Identification
                    </span>
                  </div>

                  {/* Processing status */}
                  <h3 className="text-xl font-bold text-gray-900">
                    {processingStatus}
                  </h3>

                  {/* Progress panel */}
                  <div className="bg-neutral-50 border border-gray-100 rounded-3xl p-5 space-y-3.5 text-left shadow-2xs">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400 font-black uppercase tracking-wider">
                        Progress Status
                      </span>
                      <span className="text-xs font-black text-green-700 font-mono">
                        {uploadStatus === "uploading" ? `${uploadProgress}%` : "Processing..."}
                      </span>
                    </div>

                    {/* Dynamic progress bar */}
                    <div className="h-2.5 w-full bg-gray-200/60 rounded-full overflow-hidden border border-gray-100/40 relative">
                      <div 
                        className="h-full bg-gradient-to-r from-green-500 to-emerald-450 rounded-full transition-all duration-300"
                        style={{ width: `${uploadStatus === "uploading" ? uploadProgress : 100}%` }}
                      />
                    </div>

                    {/* Speed indicator & status meta */}
                    <div className="flex justify-between items-center pt-2.5 text-[10px] font-black text-gray-400 border-t border-gray-200/50 uppercase font-mono tracking-wider">
                      <span>Status: <span className="text-green-600 font-sans font-bold">{uploadStatus}</span></span>
                      {uploadStatus === "uploading" && uploadSpeed && (
                        <span className="flex items-center gap-1">
                          Speed: <span className="text-gray-700 font-sans font-bold">{uploadSpeed}</span>
                        </span>
                      )}
                    </div>
                  </div>

                  <button
                    onClick={resetScanner}
                    className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-605 rounded-xl font-bold text-xs uppercase tracking-wider transition-colors cursor-pointer"
                  >
                    Cancel Analysis
                  </button>
                </div>
              </motion.div>

            ) : analysisResult && (!analysisResult.is_plant_related || analysisResult.validation_status === "not_plant") ? (
              <AIValidationCard
                key="validation-not-plant"
                type="not_plant"
                imagePreview={imagePreview}
                onReset={resetScanner}
                onCameraScan={() => fileInputRef.current?.click()}
              />
            ) : analysisResult && analysisResult.validation_status === "low_quality" && !bypassWarning ? (
              <AIValidationCard
                key="validation-low-quality"
                type="low_quality"
                imagePreview={imagePreview}
                qualityScore={analysisResult.image_quality_score as any || "Poor"}
                onReset={resetScanner}
                onContinue={() => setBypassWarning(true)}
              />
            ) : analysisResult && (analysisResult.validation_status === "multiple_plants" || analysisResult.multiple_plants_detected) && !bypassWarning ? (
              <AIValidationCard
                key="validation-multiple"
                type="multiple_plants"
                imagePreview={imagePreview}
                onReset={resetScanner}
                onContinue={() => setBypassWarning(true)}
              />
            ) : analysisResult ? (
              
              /* STAGE 3: Show Finished Identification Report card */
              <motion.div
                key="results"
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                className="space-y-8"
              >
                
                {/* Top Quick Status Alert for Sandbox key status */}
                {isSimulated && (
                  <div className="bg-emerald-50 rounded-2xl border border-emerald-100/80 p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-emerald-900 shadow-2xs">
                    <div className="flex items-start sm:items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-emerald-150 text-emerald-800 flex items-center justify-center font-bold text-sm shrink-0">
                        <Info className="w-4 h-4" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-xs font-extrabold text-emerald-850">Preview Sandbox Engine Active</span>
                        <span className="text-xs text-emerald-700 font-medium">To replace simulated results, update your <strong>GEMINI_API_KEY</strong> inside the Secrets Tab!</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Low Confidence or Botanical Ambiguity Warning inside Results Panel */}
                {(analysisResult.is_unable_to_identify || analysisResult.confidence < 60) && (
                  <div className="bg-sky-50 rounded-2xl border border-sky-100 p-4 flex items-start gap-3.5 text-sky-900 shadow-2xs">
                    <div className="w-8 h-8 rounded-lg bg-sky-100 text-sky-850 flex items-center justify-center font-bold text-sm shrink-0 mt-0.5 animate-pulse">
                      <Info className="w-4 h-4 text-sky-650" />
                    </div>
                    <div className="flex flex-col text-left">
                      <span className="text-xs font-black text-sky-950 uppercase tracking-wide">
                        {analysisResult.is_unable_to_identify ? "Botanical Ambiguity Encountered" : "Low Certainty Warning"}
                      </span>
                      <span className="text-xs text-sky-700 font-bold mt-0.5 leading-relaxed">
                        {analysisResult.is_unable_to_identify 
                          ? "Unable to confidently identify this plant. Showing nearest visual database match."
                          : "This result may not be fully accurate. Try uploading a clearer image for better identification."}
                      </span>
                    </div>
                  </div>
                )}

                {/* Grid Split: Picture with profile report */}
                <div className="grid grid-cols-1 md:grid-cols-5 gap-8 items-start">
                  
                  {/* Left Column: Picture assessment thumbnail */}
                  <div className="md:col-span-2 space-y-4">
                    <div className="relative rounded-2xl overflow-hidden aspect-square border border-gray-100 shadow-lg">
                      <img 
                        src={imagePreview || ""} 
                        alt={analysisResult.name} 
                        className="w-full h-full object-cover"
                        referrerPolicy="no-referrer"
                      />
                      {/* Gradient bottom mask */}
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent p-4 flex flex-col justify-end items-stretch gap-2 font-sans text-left">
                        <div>
                          <span className="text-white text-lg font-black block leading-tight">{analysisResult.name}</span>
                          <span className="text-gray-200 text-xs italic block mt-0.5">{analysisResult.scientificName}</span>
                        </div>
                        <div className={`rounded-xl px-2.5 py-1.5 text-[10px] md:text-xs font-black uppercase tracking-wider shadow-sm flex items-center justify-center gap-1.5 border border-white/20 select-none self-start ${
                          analysisResult.confidence >= 95 ? "bg-emerald-600/95 text-white" :
                          analysisResult.confidence >= 80 ? "bg-green-600/95 text-white" :
                          analysisResult.confidence >= 60 ? "bg-amber-500/95 text-white" :
                          "bg-red-550/95 text-white"
                        }`}>
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          <span>
                            {analysisResult.confidence >= 95 ? "Very High" :
                             analysisResult.confidence >= 80 ? "High" :
                             analysisResult.confidence >= 60 ? "Moderate" :
                             "Low"} ({analysisResult.confidence}%)
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3.5">
                      {isSaved ? (
                        <div className="w-full py-3 bg-green-50 border border-green-150 text-green-700 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 select-none">
                          <Check className="w-4 h-4 text-green-600 animate-bounce" />
                          <span>Specimen Saved to Collection!</span>
                        </div>
                      ) : (
                        <button
                          onClick={handleSaveToWorkspace}
                          disabled={isSaving}
                          className="w-full py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-extrabold text-sm flex items-center justify-center gap-2 transition-all cursor-pointer shadow-xs disabled:opacity-50"
                        >
                          <Sparkles className="w-4 h-4 text-green-200" />
                          <span>{isSaving ? "Saving to Cloud..." : "Save to My Collection"}</span>
                        </button>
                      )}

                      <button
                        onClick={resetScanner}
                        className="w-full py-3 bg-gray-55 border border-gray-150 text-gray-700 hover:bg-gray-100 rounded-xl font-black text-xs uppercase tracking-wider flex items-center justify-center gap-2 select-none transition-all cursor-pointer"
                      >
                        <RefreshCw className="w-4 h-4 text-gray-500" />
                        Scan Another Plant
                      </button>
                    </div>
                  </div>

                  {/* Right Column: Full Tabular Analytics Panels */}
                  <div className="md:col-span-3 space-y-6">
                    
                    {/* Modern UI Tab headers */}
                    <div className="flex border-b border-gray-150 overflow-x-auto pb-0.5 gap-1">
                      {[
                        { id: "profile", label: "Specimen Profile", icon: FileText },
                        { id: "care", label: "Optimized Care", icon: Heart },
                        { id: "health", label: "Health Diagnostic", icon: Zap }
                      ].map((item) => {
                        const Icon = item.icon;
                        const isCurrent = activeTab === item.id;
                        return (
                          <button
                            key={item.id}
                            onClick={() => setActiveTab(item.id as any)}
                            className={`py-3 px-4 text-xs font-bold flex items-center gap-2 tracking-wide border-b-2 transition-all rounded-t-lg shrink-0 cursor-pointer ${
                              isCurrent
                                ? "border-green-600 text-green-700 bg-green-50/20"
                                : "border-transparent text-gray-500 hover:text-gray-900"
                            }`}
                          >
                            <Icon className={`w-4 h-4 ${isCurrent ? "text-green-500" : "text-gray-400"}`} />
                            {item.label}
                          </button>
                        );
                      })}
                    </div>

                    {/* TAB PANEL CONTENT */}
                    <div className="bg-gray-50/40 rounded-2xl border border-gray-100 p-6 min-h-[300px]">
                      
                      {/* 1. Leaf Profile Tab Content */}
                      {activeTab === "profile" && (
                        <div className="space-y-5">
                          <div>
                            <span className="text-[10px] font-extrabold text-green-700 uppercase tracking-widest block mb-1">
                              Species Taxonomy
                            </span>
                            <h3 className="text-3xl font-extrabold text-gray-900 tracking-tight leading-none">
                              {analysisResult.name}
                            </h3>
                            <span className="text-sm text-gray-500 font-bold italic mt-1 block">
                              {analysisResult.scientificName}
                            </span>
                          </div>

                          {analysisResult.commonNames && analysisResult.commonNames.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 items-center">
                              <span className="text-xs text-gray-400 font-semibold mr-1">Commons:</span>
                              {analysisResult.commonNames.map((name) => (
                                <span key={name} className="px-2.5 py-0.5 bg-white text-gray-700 text-xs font-medium rounded-md border border-gray-150">
                                  {name}
                                </span>
                              ))}
                            </div>
                          )}

                          <div className="border-t border-gray-150/60 pt-4">
                            <h4 className="text-xs font-extrabold text-gray-700 uppercase tracking-widest mb-2 flex items-center gap-1.5">
                              <Search className="w-3.5 h-3.5 text-green-500" />
                              Species Overview
                            </h4>
                            <p className="text-sm text-gray-600 font-medium leading-relaxed">
                              {analysisResult.description}
                            </p>
                          </div>

                          <div className="bg-gradient-to-tr from-green-50 to-emerald-50 rounded-xl border border-green-100 p-4 relative overflow-hidden">
                            <div className="absolute right-3 top-3 w-12 h-12 bg-green-200/20 rounded-full flex items-center justify-center text-green-500 blur-xs">
                              <Sparkles className="w-6 h-6" />
                            </div>
                            <h5 className="text-xs font-bold text-green-800 uppercase tracking-wider flex items-center gap-1">
                              <Sparkles className="w-3.5 h-3.5" />
                              Botanical Trivia
                            </h5>
                            <p className="mt-1.5 text-xs text-green-700 font-medium leading-relaxed pr-6">
                              {analysisResult.funFact}
                            </p>
                          </div>

                        </div>
                      )}

                      {/* 2. Optimized Care Tab Content */}
                      {activeTab === "care" && (
                        <div className="space-y-6">
                          <div>
                            <span className="text-[10px] font-extrabold text-green-700 uppercase tracking-widest block mb-2">
                              Gardening Requirements
                            </span>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-gray-500 font-bold">Difficulty:</span>
                              <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold uppercase ${
                                analysisResult.care.difficulty?.toLowerCase().includes("easy")
                                  ? "bg-green-100 text-green-850"
                                  : analysisResult.care.difficulty?.toLowerCase().includes("medium")
                                  ? "bg-amber-100 text-amber-850"
                                  : "bg-red-105 text-red-850"
                              }`}>
                                {analysisResult.care.difficulty}
                              </span>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 border-t border-gray-150/60 pt-4">
                            
                            {/* Watering */}
                            <div className="bg-white p-4 rounded-xl border border-gray-100 space-y-2">
                              <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                                <Droplet className="w-4 h-4 text-blue-500" />
                              </div>
                              <span className="text-xs font-bold text-gray-400 block uppercase tracking-wider">Watering</span>
                              <p className="text-xs text-gray-700 font-semibold leading-relaxed">
                                {analysisResult.care.watering}
                              </p>
                            </div>

                            {/* Sunlight */}
                            <div className="bg-white p-4 rounded-xl border border-gray-100 space-y-2">
                              <div className="w-8 h-8 rounded-lg bg-amber-55 text-amber-600 flex items-center justify-center">
                                <Sun className="w-4 h-4 text-amber-500" />
                              </div>
                              <span className="text-xs font-bold text-gray-400 block uppercase tracking-wider">Sunlight</span>
                              <p className="text-xs text-gray-700 font-semibold leading-relaxed">
                                {analysisResult.care.sunlight}
                              </p>
                            </div>

                            {/* Temperature */}
                            <div className="bg-white p-4 rounded-xl border border-gray-100 space-y-2">
                              <div className="w-8 h-8 rounded-lg bg-rose-50 text-rose-600 flex items-center justify-center">
                                <Thermometer className="w-4 h-4 text-rose-500" />
                              </div>
                              <span className="text-xs font-bold text-gray-400 block uppercase tracking-wider">Temperature</span>
                              <p className="text-xs text-gray-700 font-semibold leading-relaxed">
                                {analysisResult.care.temperature}
                              </p>
                            </div>

                          </div>
                        </div>
                      )}

                      {/* 3. Diagnostics & Health Content */}
                      {activeTab === "health" && (
                        <div className="space-y-5">
                          <div className="flex items-center justify-between border-b border-gray-150/60 pb-3">
                            <span className="text-xs font-extrabold text-gray-400 uppercase tracking-widest">
                              Pathological assessment
                            </span>
                            <div className="flex items-center gap-1.5">
                              <span className="text-xs font-bold text-gray-500">Status:</span>
                              <span className={`px-2.5 py-0.5 rounded-full text-xs font-extrabold uppercase ${
                                analysisResult.healthAssessment.status?.toLowerCase().includes("healthy")
                                  ? "bg-green-100 text-green-800"
                                  : "bg-amber-100 text-amber-800"
                              }`}>
                                {analysisResult.healthAssessment.status}
                              </span>
                            </div>
                          </div>

                          <div className="space-y-4">
                            <span className="text-xs font-bold text-gray-500 uppercase tracking-widest block">
                              Diagnostic Diagnostics & Treatment Notes
                            </span>
                            <p className="text-sm text-gray-600 font-medium leading-relaxed">
                              {analysisResult.healthAssessment.details}
                            </p>
                          </div>

                          <div className="bg-white p-4 rounded-xl border border-gray-150/40 mt-4 flex items-start gap-3">
                            <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                            <div>
                              <span className="text-xs font-extrabold text-gray-800 block uppercase tracking-wider">Proactive Advice</span>
                              <span className="text-xs text-gray-500 font-medium block mt-1">To ensure maximum cell respiration, regularly wipe both sides of dust-gathering leaves using a damp micro-fiber cloth.</span>
                            </div>
                          </div>
                        </div>
                      )}

                    </div>

                  </div>
                </div>

              </motion.div>

            ) : null}
          </AnimatePresence>

          {/* User Error Banner */}
          {errorMsg && (
            <div className="mt-6 bg-red-50 text-red-800 text-xs sm:text-sm rounded-xl p-4 flex items-start gap-2.5 border border-red-150 shadow-2xs">
              <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
              <div>
                <span className="font-bold block">Specimen Error</span>
                <p className="text-red-700 mt-1 font-medium">{errorMsg}</p>
              </div>
            </div>
          )}

          {/* Account benefits promotion bar for non-logged-in users */}
          {!currentUser && (
            <div className="mt-6 bg-green-50/50 border border-green-150 rounded-2xl p-4 sm:p-5 flex flex-col sm:flex-row items-center justify-between gap-4 select-none">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center text-green-700 shrink-0 border border-green-200">
                  <Leaf className="w-5 h-5 text-green-600 animate-pulse" />
                </div>
                <div className="text-left">
                  <p className="text-xs sm:text-sm font-bold text-gray-900 leading-snug">
                    Create a free account to save your identification results and build your personal plant collection.
                  </p>
                  <p className="text-[11px] text-gray-500 mt-0.5">
                    Safe, private, and syncs automatically across all your devices.
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  const benefitsEl = document.getElementById("account-benefits-section");
                  if (benefitsEl) {
                    benefitsEl.scrollIntoView({ behavior: "smooth" });
                  } else {
                    // Fallback: Trigger sign in modal
                    const signinBtn = document.getElementById("signin-btn");
                    signinBtn?.click();
                  }
                }}
                className="w-full sm:w-auto px-5 py-2.5 bg-green-600 hover:bg-green-700 hover:text-white text-white font-extrabold text-xs uppercase tracking-wide rounded-xl transition-all shadow-3xs cursor-pointer min-h-[38px] flex items-center justify-center whitespace-nowrap"
              >
                Learn More
              </button>
            </div>
          )}

        </div>

      </div>
    </section>
  );
}
